import React from 'react'
import { useTokenExpiration, formatTimeRemaining } from '../../utils/token'

// Session card: user identity, session countdown and logout/renew actions.
// Visual language follows the sidebar's Franquia card (neutral surface, small
// uppercase header); red is reserved for the expiring state.

const SessionCard = ({ token, userName, userEmail, isAdmin, onLogout, onRenew, isRenewing }) => {
    // 1s tick: the mm:ss countdown in the expiring state needs it.
    const { timeRemaining, totalTokenTime, isExpiring } = useTokenExpiration(token, onLogout, 1000);

    if (!timeRemaining) return null;

    // Name as the title with the e-mail as a subtitle; without a name the e-mail is the title.
    const displayName = userName || userEmail || 'Usuário';
    const subtitle = userName && userEmail ? userEmail : null;

    const totalSeconds = Math.ceil(timeRemaining / 1000);
    const totalMinutes = Math.ceil(totalSeconds / 60);

    // Progress starts at 100% and shrinks as the session runs out.
    const progressPercent = totalTokenTime
        ? Math.max(0, Math.min(100, (timeRemaining / totalTokenTime) * 100))
        : Math.max(0, Math.min(100, (timeRemaining / (30 * 60 * 1000)) * 100)); // Fallback 30 min

    let timeText;
    if (totalSeconds <= 0) {
        timeText = 'Expirando agora...';
    } else if (isExpiring) {
        timeText = `Expira em ${formatTimeRemaining(timeRemaining)}`;
    } else {
        timeText = `Expira em ${totalMinutes} min`;
    }

    const containerColor = isExpiring
        ? 'bg-red-50/70 dark:bg-red-950/20 border-red-200 dark:border-red-800/40'
        : 'bg-gray-50/80 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700';

    const avatarColor = isExpiring
        ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
        : 'bg-blue-50 dark:bg-blue-900/25 text-blue-600 dark:text-blue-400';

    const timeColor = isExpiring
        ? 'text-red-600 dark:text-red-400 font-medium'
        : 'text-gray-500 dark:text-gray-400';

    const barColor = isExpiring ? 'bg-red-500' : 'bg-blue-500';

    const dividerColor = isExpiring
        ? 'bg-red-200/70 dark:bg-red-800/30'
        : 'bg-gray-200/70 dark:bg-gray-700/60';

    return (
        <div className={`${containerColor} border rounded-lg px-3 py-2.5 space-y-2 transition-colors`}>
            {/* Header: label on the left, actions on the right */}
            <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 min-w-0">
                    <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                        <span className="material-icons" style={{ fontSize: 12 }}>schedule</span>
                        Sessão
                    </span>
                    {isAdmin && (
                        <span className="flex-shrink-0 px-1.5 py-0.5 bg-green-50 dark:bg-green-900/25 text-green-700 dark:text-green-400 rounded text-[10px] leading-none font-semibold whitespace-nowrap">
                            admin
                        </span>
                    )}
                </span>
                <div className="flex gap-1 flex-shrink-0 -my-1">
                    {/* Renew is only offered once the session is about to expire */}
                    {isExpiring && onRenew && (
                        <button
                            onClick={onRenew}
                            disabled={isRenewing}
                            className="w-6 h-6 flex items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/25 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-40 transition"
                            title="Renovar sessão"
                        >
                            <span className={`material-icons ${isRenewing ? 'animate-spin' : ''}`} style={{ fontSize: '15px' }}>refresh</span>
                        </button>
                    )}
                    <button
                        onClick={onLogout}
                        className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                        title="Sair"
                    >
                        <span className="material-icons" style={{ fontSize: '15px' }}>logout</span>
                    </button>
                </div>
            </div>
            <div className={`h-px ${dividerColor}`} />

            {/* User */}
            <div className="flex items-center gap-2.5 min-w-0">
                <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${avatarColor}`}>
                    <span className="material-icons" style={{ fontSize: '26px' }}>account_circle</span>
                </span>
                <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-gray-800 dark:text-gray-100 truncate" title={displayName}>{displayName}</p>
                    {subtitle && (
                        <p className="text-[11px] leading-tight text-gray-400 dark:text-gray-500 truncate" title={subtitle}>{subtitle}</p>
                    )}
                </div>
            </div>

            {/* Progress bar + remaining time */}
            <div>
                <div className="w-full bg-gray-200/70 dark:bg-gray-700/50 rounded-full h-1 overflow-hidden">
                    <div
                        className={`h-full ${barColor} rounded-full transition-all duration-1000`}
                        style={{ width: `${Math.max(0, progressPercent)}%` }}
                    ></div>
                </div>
                <p className={`mt-1 text-right text-[10px] leading-none tabular-nums ${timeColor}`}>{timeText}</p>
            </div>
        </div>
    );
};

export default SessionCard
